/**
 * Coach advice types — the analyzer's output surface. Mirrors sudoku's `Hint`
 * pattern: pure data carrying i18n KEYS + interpolation params, never prebuilt
 * text, so the DOM panel owns all rendering via `t()`. Nothing here touches
 * canvas or the DOM.
 *
 * `HandShape` is a GREEDY, best-effort decomposition for teaching/display only —
 * it is NOT the source of truth for actual wins (that stays `WinChecker`'s
 * exhaustive backtracking). A greedy grouping can miss the optimal split; that
 * is acceptable for "here's roughly what your hand looks like" coaching.
 */

import type { Tile } from '../game/types.js';

/** A concealed group the greedy analyzer pulled out of the hand. */
export interface CompleteSet {
  readonly type: 'run' | 'triplet';
  readonly tiles: readonly Tile[];
}

/** Greedy structural summary of the local hand (for display, not win-checking). */
export interface HandShape {
  /** Open (called) melds already exposed — count only, they're fixed. */
  readonly meldCount: number;
  /** Concealed triplets + runs found greedily. */
  readonly completeSets: readonly CompleteSet[];
  /** A pair candidate if one remains after sets, else null. */
  readonly pair: readonly Tile[] | null;
  /** Tiles contributing to no near-complete group. */
  readonly floaters: readonly Tile[];
}

/** i18n reason codes for a suggested discard — each maps to a `usefulness` term. */
export const DISCARD_REASON = {
  /** A single wind/dragon: honours never run, so it's dead weight. */
  loneHonor: 'coach.reason.loneHonor',
  /** A 1 or 9: terminals connect fewer ways than middle tiles. */
  terminal: 'coach.reason.terminal',
  /** A suit tile with no adjacent/pair support. */
  noConnection: 'coach.reason.noConnection',
  /** A 4th copy of a kind — a set needs only three, so one is spare. */
  excessCopy: 'coach.reason.excessCopy',
} as const;

/** i18n reason codes for an available claim. */
export const CLAIM_REASON = {
  ron: 'coach.claim.ron',
  pung: 'coach.claim.pung',
  chi: 'coach.claim.chi',
} as const;

/** A suggested discard: the tile, plus WHY (i18n key + params for the panel). */
export interface DiscardAdvice {
  readonly tile: Tile;
  readonly reasonKey: string;
  readonly reasonParams?: Record<string, unknown>;
}

export type ClaimKind = 'ron' | 'pung' | 'chi';

/** Advice for one available claim during the claim window. */
export interface ClaimAdvice {
  readonly claimType: ClaimKind;
  readonly reasonKey: string;
  readonly reasonParams?: Record<string, unknown>;
}

/** Top-level bundle the UI consumes for the local seat. */
export interface CoachAdvice {
  readonly handShape: HandShape;
  /** Null unless it's the local seat's turn to discard. */
  readonly discardAdvice: DiscardAdvice | null;
  /** Null unless the local seat has a claim on the open discard. */
  readonly claimAdvice: readonly ClaimAdvice[] | null;
}
